<template>
    <div class="update-detail" v-if="clone">
        <div class="section">
            <h3>{{ $t("message.channelSettings") || "Kanal Ayarları" }}</h3>
            
            <div class="form-group">
                <label>{{ $t("whatsappChannel.channelName") }}</label>
                <a-input v-model="clone.name" :placeholder="$t('whatsappChannel.channelName')" />
            </div>

            <div class="form-group">
                <label>{{ $t("message.apiKeyMode") || "API Anahtarı Modu" }}</label>
                <a-tag :color="clone.payload.keyMode === 'byok' ? 'blue' : 'green'" class="key-mode-badge">
                    {{ clone.payload.keyMode === 'byok'
                        ? ($t("message.byokTitle") || "Kendi API Anahtarımı Kullanacağım (BYOK)")
                        : ($t("message.supsisKeyTitle") || "Supsis AI API Anahtarı Sağlasın") }}
                </a-tag>
                <div class="help-text">
                    <a-icon type="info-circle" />
                    <span>{{ $t("auto.openRouterKeyModeCannotChange") }}</span>
                </div>
            </div>

            <div class="form-group" v-if="clone.payload.keyMode === 'byok'">
                <label>{{ $t("message.openrouterApiKey") || "OpenRouter API Key" }}</label>
                <a-input-password 
                    v-model="clone.payload.apiKey" 
                    :placeholder="$t('message.openrouterApiKey')"
                />
                <div class="help-text">
                    <a-icon type="info-circle" />
                    <span>
                        {{ $t("message.openrouterApiKeyHint") || "API anahtarınızı" }}
                        <a href="https://openrouter.ai/keys" target="_blank">OpenRouter Dashboard</a>
                        {{ "üzerinden alabilirsiniz." }}
                    </span>
                </div>
            </div>

            <div class="form-group">
                <label>{{ $t("message.status") || "Durum" }}</label>
                <a-switch v-model="clone.enabled" />
                <span class="status-text">{{ clone.enabled ? $t("auto.active") : $t("auto.inactive") }}</span>
            </div>
        </div>

        <div class="section">
            <h3>{{ $t("message.providerInfo") || "Provider Bilgisi" }}</h3>
            <div class="provider-info-card">
                <img :src="providerLogo" :alt="providerName" class="provider-logo" />
                <div class="provider-details">
                    <div class="provider-name">{{ providerName }}</div>
                    <div class="provider-slug">{{ clone.payload.provider }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash";

const PROVIDER_INFO = {
    openrouter: { name: "OpenRouter", logo: "https://static.supsis.live/common/ai/openrouter.svg" },
    openai: { name: "OpenAI", logo: "https://static.supsis.live/common/ai/openai.svg" },
    google: { name: "Google AI Studio", logo: "https://static.supsis.live/common/ai/google.svg" },
    anthropic: { name: "Anthropic", logo: "https://static.supsis.live/common/ai/anthropic.svg" },
    mistral: { name: "Mistral", logo: "https://static.supsis.live/common/ai/mistral.webp" },
    azure: { name: "Microsoft Azure", logo: "https://static.supsis.live/common/ai/microsoft.svg" },
    bedrock: { name: "Amazon Bedrock", logo: "https://static.supsis.live/common/ai/aws.svg" },
    cohere: { name: "Cohere", logo: "https://static.supsis.live/common/ai/cohere.svg" },
    xai: { name: "xAI", logo: "https://static.supsis.live/common/ai/xai.svg" },
    deepinfra: { name: "DeepInfra", logo: "https://static.supsis.live/common/ai/deepinfra.webp" },
    together: { name: "Together", logo: "https://static.supsis.live/common/ai/togetherai.svg" },
    fireworks: { name: "Fireworks", logo: "https://static.supsis.live/common/ai/fireworks.svg" },
    siliconflow: { name: "SiliconFlow", logo: "https://static.supsis.live/common/ai/siliconflow.png" },
    parasail: { name: "Parasail", logo: "https://static.supsis.live/common/ai/parasail.svg" },
    nebius: { name: "Nebius", logo: "https://static.supsis.live/common/ai/nebius.svg" },
    alibaba: { name: "Alibaba Cloud", logo: "https://static.supsis.live/common/ai/alibaba.svg" },
    xiaomi: { name: "Xiaomi", logo: "https://static.supsis.live/common/ai/xiaomi.svg" },
    sambanova: { name: "SambaNova", logo: "https://static.supsis.live/common/ai/sambanova.webp" },
    moonshot: { name: "Moonshot AI", logo: "https://static.supsis.live/common/ai/moonshot.svg" },
    minimax: { name: "Minimax", logo: "https://static.supsis.live/common/ai/minimax.svg" },
    nvidia: { name: "NVIDIA", logo: "https://static.supsis.live/common/ai/nvidia.svg" },
};

export default {
    name: "UpdateDetail",
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            clone: null,
        };
    },
    computed: {
        providerName() {
            const provider = _.get(this.clone, "payload.provider", "openrouter");
            return _.get(PROVIDER_INFO, [provider, "name"], "OpenRouter");
        },
        providerLogo() {
            const provider = _.get(this.clone, "payload.provider", "openrouter");
            return _.get(PROVIDER_INFO, [provider, "logo"], PROVIDER_INFO.openrouter.logo);
        },
    },
    watch: {
        value: {
            handler(val) {
                this.clone = _.cloneDeep(val);
            },
            immediate: true,
            deep: true,
        },
        clone: {
            handler(val) {
                if (val && !_.isEqual(val, this.value)) {
                    this.$emit("input", val);
                }
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.update-detail {
    padding: 20px;
    max-width: 600px;

    .section {
        margin-bottom: 30px;
        padding: 20px;
        background: var(--color-bg-default);
        border-radius: 8px;
        border: 1px solid var(--color-border-default);

        h3 {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 1px solid var(--color-border-default);
        }
    }

    .form-group {
        margin-bottom: 20px;

        label {
            display: block;
            font-weight: 500;
            margin-bottom: 8px;
            color: var(--color-text-default);
        }

        .key-mode-badge {
            font-size: 13px;
            padding: 4px 10px;
            margin-bottom: 6px;
        }

        .help-text {
            margin-top: 8px;
            font-size: 12px;
            color: var(--color-text-muted);
            display: flex;
            align-items: center;
            gap: 5px;

            a {
                color: var(--color-primary);
            }
        }

        .status-text {
            margin-left: 10px;
            font-size: 14px;
        }
    }

    .provider-info-card {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 15px;
        background: var(--color-bg-subtle);
        border-radius: 8px;

        .provider-logo {
            width: 50px;
            height: 50px;
            object-fit: contain;
        }

        .provider-details {
            .provider-name {
                font-size: 16px;
                font-weight: 600;
            }

            .provider-slug {
                font-size: 12px;
                color: var(--color-text-muted);
            }
        }
    }
}
</style>
